// =============================================================================
// legal-entity-client.ts — Client API HIÉRARCHIE MULTI-ÉTABLISSEMENT (SIREN), DOUBLABLE.
// =============================================================================
// T7-MULTI-ETAB (REF 1.7 §5, LECTURE SEULE). Miroir de l'endpoint ADMIN neuf
// `GET /admin/legal-entities/:siren/establishments` (EstablishmentHierarchyController,
// read-model cross-fournisseur sous withAdmin — plan ARCH §1.1). Comme les autres
// clients UI (supplier, legal-admin, participations), expose une INTERFACE consommée
// par les hooks TanStack ; l'impl HTTP réelle et le double stub vivent derrière la
// MÊME interface (stub-first).
//
// DISJONCTION T6 (mandat ORCH) : ce client est un JEU DE FICHIERS NEUF, TOTALEMENT
// DISTINCT de `supplier-client.*` (édité par T6). Il ne l'importe pas et ne le touche
// pas. L'enum d'état est RÉPLIQUÉ localement (couplage ZÉRO — plan ARCH §2.1), quitte
// à dupliquer un union de littéraux structurellement identique.
//
// SÉCURITÉ / PII (plan ARCH §3) : la vue T7 ne projette QUE des champs SIRENE publics
// + états OWNED supplier-reference. AUCUN contact nominatif ici (minimisation PII : la
// fiche A-04 reste le point d'accès gardé aux interlocuteurs). READ-ONLY : aucune
// méthode d'écriture n'existe sur ce contrat (invariant SIRET = SIREN + NIC, GATE ARCH).

import { HttpApiError } from '@/api/http-error';

/**
 * État établissement (6 états, parité Prisma EstablishmentState). RÉPLIQUÉ localement
 * (couplage zéro vis-à-vis de `supplier-client.ts`, T6). Structurellement identique à
 * l'union miroir du client fournisseur : reste assignable aux mappings de libellés partagés.
 */
export type EstablishmentStateDto =
  | 'Brouillon'
  | 'Soumis'
  | 'Refuse'
  | 'Actif'
  | 'Suspendu'
  | 'Archive';

/**
 * Un établissement (SIRET) rattaché à un SIREN, dans la vue groupée T7. Champs OWNED
 * supplier-reference UNIQUEMENT (plan ARCH §1.1) : SIRET, état, date d'enrôlement.
 * `enrolledAt` = ISO 8601 `string | null` (la SPA ne couple pas au type `Date` du back ;
 * `null` tant qu'aucun enrôlement horodaté n'est connu).
 */
export interface LegalEntityEstablishmentDto {
  readonly siret: string;
  readonly etablissementState: EstablishmentStateDto;
  readonly enrolledAt: string | null;
}

/**
 * Vue groupée d'une personne morale (SIREN) et de ses établissements (miroir HTTP de
 * `EstablishmentHierarchyView`, plan ARCH §1.1). `raisonSocialeLegale` = dénomination
 * racine (`null` si la personne morale racine n'est pas enrichie). `etablissements` =
 * tous les SIRET du SIREN visibles sous withAdmin (cross-fournisseur admin-only).
 */
export interface LegalEntityHierarchyDto {
  readonly siren: string;
  readonly raisonSocialeLegale: string | null;
  readonly etablissements: readonly LegalEntityEstablishmentDto[];
}

/**
 * Erreur applicative renvoyée par l'API hiérarchie (HTTP 4xx). Porte le `status` HTTP
 * pour que l'UI mappe par code (400 = SIREN invalide ; 404 = SIREN sans établissement).
 * L'UI ne réinterprète jamais la règle métier (autorité = API).
 */
export class LegalEntityApiError extends HttpApiError {
  public constructor(
    message: string,
    /** Code HTTP de la réponse d'erreur (400/404…), `undefined` si indisponible. */
    status?: number,
  ) {
    super(message, status, 'LegalEntityApiError');
  }
}

/**
 * Contrat du client HIÉRARCHIE MULTI-ÉTABLISSEMENT — LECTURE SEULE (T7). Aucune règle
 * métier ici : la lecture cross-fournisseur (withAdmin, RLS) est l'autorité de l'API.
 */
export interface LegalEntityClient {
  /**
   * GET /admin/legal-entities/:siren/establishments — vue groupée des établissements
   * d'un SIREN (read-only, withAdmin côté API). 400 (SIREN ≠ 9 chiffres) / 404 (aucun
   * établissement) → LegalEntityApiError(status).
   */
  getHierarchy(siren: string): Promise<LegalEntityHierarchyDto>;
}
